import React from 'react';
import type { WorkModelHubStats } from '../../../types/work-model-hub';
import type { HubCrossLink } from '../../../types/hub-cross-link';
import { HubCrossLinks } from '../../../components/hub/HubCrossLinks';
import { buildWorkModelHubPath } from '../../../utils/work-model/build-work-model-hub-path';
import { buildRoleHubPath } from '../../../utils/role/role-hub-config';
import { buildLocationSrpPath } from '../../../utils/location/build-location-srp-path';
import { companyProfileUrlPath } from '../../../utils/routing/company-profile-url-path';
import { buildHubIndexLinks, cityInCopy, hubLinksHeading } from '../../../utils/seo/hub-index-links';
import { getFinnishRoleGenitive } from '../../../utils/i18n/finnish-role-genitive';
import { finnishRolePhrase } from '../../../utils/seo/generate-srp-seo-meta';
import { getCategoryDisplayName } from '../../../utils/category/category-hub-config';
import { LOCALIZED_PATHS } from '../../../utils/constants/url-constants';

type Locale = 'en' | 'fi' | 'sv';

// In-code locale maps, like RoleHeader.tsx: this header is part of the server-rendered SRP shell.
const T: Record<Locale, {
  eyebrowRemote: string;
  eyebrowHybrid: string;
  remoteH1: string;
  hybridH1: string;
  remoteRoleH1: (role: string) => string;
  hybridRoleH1: (role: string) => string;
  remoteCityH1: (city: string) => string;
  hybridCityH1: (city: string) => string;
  remoteCategoryH1: (cat: string) => string;
  hybridCategoryH1: (cat: string) => string;
  openNow: string;
  remoteIntro: (n: string) => string;
  hybridIntro: (n: string) => string;
  shareTitle: string;
  shareOfAll: (pct: string) => string;
  remoteLabel: string;
  hybridLabel: string;
  onsiteLabel: string;
  englishShare: (pct: string) => string;
  roles: string;
  fields: string;
  cities: string;
  employers: string;
  allRemote: string;
  allHybrid: string;
  roleAll: (role: string) => string;
  cityAll: (city: string) => string;
}> = {
  fi: {
    eyebrowRemote: 'Etätyö · Suomi',
    eyebrowHybrid: 'Hybridityö · Suomi',
    remoteH1: 'Etätyöpaikat Suomessa',
    hybridH1: 'Hybridityöpaikat Suomessa',
    remoteRoleH1: (role) => finnishRolePhrase(role, 'etätyöt'),
    hybridRoleH1: (role) => finnishRolePhrase(role, 'hybridityöt'),
    remoteCityH1: (city) => `Etätyöpaikat ${city}`,
    hybridCityH1: (city) => `Hybridityöpaikat ${city}`,
    remoteCategoryH1: (cat) => `Etätyöt: ${cat}`,
    hybridCategoryH1: (cat) => `Hybridityöt: ${cat}`,
    openNow: 'avointa työpaikkaa juuri nyt',
    remoteIntro: (n) =>
      `JobCrawls kokoaa etätyöpaikat suoraan työnantajien sivuilta ja Työmarkkinatorilta yhteen hakuun: ${n} kokonaan etänä tehtävää työtä. Ilmoitukset päivittyvät päivittäin.`,
    hybridIntro: (n) =>
      `JobCrawls kokoaa hybridityöpaikat suoraan työnantajien sivuilta ja Työmarkkinatorilta yhteen hakuun: ${n} työtä, joissa osa viikosta tehdään etänä ja osa toimistolla. Ilmoitukset päivittyvät päivittäin.`,
    shareTitle: 'Työmalli Suomen avoimissa työpaikoissa',
    shareOfAll: (pct) => `${pct} % kaikista työpaikoista`,
    remoteLabel: 'Etätyö',
    hybridLabel: 'Hybridityö',
    onsiteLabel: 'Paikkasidonnainen',
    englishShare: (pct) => `${pct} % ilmoituksista on englanniksi`,
    roles: 'Suosituimmat ammatit',
    fields: 'Alat',
    cities: 'Kaupungit',
    employers: 'Eniten rekrytoivat',
    allRemote: 'Kaikki etätyöpaikat',
    allHybrid: 'Kaikki hybridityöpaikat',
    roleAll: (role) => `Kaikki ${role} työpaikat`,
    cityAll: (city) => `Kaikki työpaikat ${city}`,
  },
  sv: {
    eyebrowRemote: 'Distans · Finland',
    eyebrowHybrid: 'Hybrid · Finland',
    remoteH1: 'Distansjobb i Finland',
    hybridH1: 'Hybridjobb i Finland',
    remoteRoleH1: (role) => `${role} på distans`,
    hybridRoleH1: (role) => `${role}, hybridjobb`,
    remoteCityH1: (city) => `Distansjobb i ${city}`,
    hybridCityH1: (city) => `Hybridjobb i ${city}`,
    remoteCategoryH1: (cat) => `Distansjobb: ${cat}`,
    hybridCategoryH1: (cat) => `Hybridjobb: ${cat}`,
    openNow: 'lediga tjänster just nu',
    remoteIntro: (n) =>
      `JobCrawls samlar distansjobb direkt från arbetsgivarnas sidor och Työmarkkinatori i en sökning: ${n} helt distansbaserade tjänster. Annonserna uppdateras dagligen.`,
    hybridIntro: (n) =>
      `JobCrawls samlar hybridjobb direkt från arbetsgivarnas sidor och Työmarkkinatori i en sökning: ${n} tjänster där en del av veckan görs på distans och en del på kontoret. Annonserna uppdateras dagligen.`,
    shareTitle: 'Arbetsmodell i Finlands lediga jobb',
    shareOfAll: (pct) => `${pct} % av alla jobb`,
    remoteLabel: 'Distans',
    hybridLabel: 'Hybrid',
    onsiteLabel: 'På plats',
    englishShare: (pct) => `${pct} % av annonserna är på engelska`,
    roles: 'Populära yrken',
    fields: 'Branscher',
    cities: 'Städer',
    employers: 'Rekryterar mest',
    allRemote: 'Alla distansjobb',
    allHybrid: 'Alla hybridjobb',
    roleAll: (role) => `Alla jobb som ${role}`,
    cityAll: (city) => `Alla lediga jobb i ${city}`,
  },
  en: {
    eyebrowRemote: 'Remote · Finland',
    eyebrowHybrid: 'Hybrid · Finland',
    remoteH1: 'Remote Jobs in Finland',
    hybridH1: 'Hybrid Jobs in Finland',
    remoteRoleH1: (role) => `Remote ${role} Jobs`,
    hybridRoleH1: (role) => `Hybrid ${role} Jobs`,
    remoteCityH1: (city) => `Remote Jobs in ${city}`,
    hybridCityH1: (city) => `Hybrid Jobs in ${city}`,
    remoteCategoryH1: (cat) => `Remote ${cat} Jobs`,
    hybridCategoryH1: (cat) => `Hybrid ${cat} Jobs`,
    openNow: 'open positions right now',
    remoteIntro: (n) =>
      `JobCrawls collects remote jobs straight from employers' own sites and Työmarkkinatori into one search: ${n} fully remote positions. Listings refresh daily.`,
    hybridIntro: (n) =>
      `JobCrawls collects hybrid jobs straight from employers' own sites and Työmarkkinatori into one search: ${n} positions split between remote days and the office. Listings refresh daily.`,
    shareTitle: 'Work model across open jobs in Finland',
    shareOfAll: (pct) => `${pct}% of all jobs`,
    remoteLabel: 'Remote',
    hybridLabel: 'Hybrid',
    onsiteLabel: 'On-site',
    englishShare: (pct) => `${pct}% of the listings are in English`,
    roles: 'Popular roles',
    fields: 'Fields',
    cities: 'Cities',
    employers: 'Hiring most',
    allRemote: 'All remote jobs',
    allHybrid: 'All hybrid jobs',
    roleAll: (role) => `All ${role} jobs`,
    cityAll: (city) => `All jobs in ${city}`,
  },
};

function formatCount(n: number, locale: Locale): string {
  return locale === 'en' ? n.toLocaleString('en-US') : n.toLocaleString('fi-FI').replace(/ /g, ' ');
}

function roleInCopy(name: string, locale: Locale): string {
  if (locale !== 'fi') return name;
  const gen = getFinnishRoleGenitive(name);
  return gen === name.trim() ? name : gen;
}

/** H1 of a work-model page, by context (bare hub, x role, x city, x category). Exported for tests. */
export function buildWorkModelHeaderH1(stats: WorkModelHubStats, locale: Locale): string {
  const t = T[locale];
  const remote = stats.key === 'remote';
  const { role, city, category } = stats.context;
  if (role) return remote ? t.remoteRoleH1(role.name) : t.hybridRoleH1(role.name);
  if (city) return remote ? t.remoteCityH1(cityInCopy(city.name, locale)) : t.hybridCityH1(cityInCopy(city.name, locale));
  if (category) return remote ? t.remoteCategoryH1(category.name) : t.hybridCategoryH1(category.name);
  return remote ? t.remoteH1 : t.hybridH1;
}

interface RankedRow {
  href: string;
  label: string;
  count: number;
}

/** A ranked list card: label, count and a bar proportional to the largest row, so the shape of the market reads at a glance. */
function RankedList({ heading, rows, locale, tone }: { heading: string; rows: RankedRow[]; locale: Locale; tone: 'green' | 'amber' | 'slate' }) {
  if (rows.length === 0) return null;
  const max = Math.max(...rows.map((r) => r.count), 1);
  const bar =
    tone === 'green'
      ? 'bg-[#3B5E45] dark:bg-[#98CFA8]'
      : tone === 'amber'
        ? 'bg-[#B98A1B] dark:bg-[#E9C46A]'
        : 'bg-[#6F7F8F] dark:bg-[#9CC6E6]';
  return (
    <section className="rounded-2xl border border-[#DDD5C8]/80 bg-white/85 p-4 dark:border-[#4A3520] dark:bg-[#1F1509]/90 sm:p-5" aria-label={heading}>
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7A6E5F] dark:text-[#B8A48C]">{heading}</h2>
      <ol className="mt-3 space-y-2.5">
        {rows.map((r) => (
          <li key={r.href}>
            <a href={r.href} className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B5E45] rounded-md">
              <span className="flex items-baseline justify-between gap-3">
                <span className="truncate text-sm font-medium text-[#1E1A14] group-hover:text-[#2F4A38] group-hover:underline underline-offset-2 dark:text-[#F2EADF] dark:group-hover:text-[#98CFA8]">
                  {r.label}
                </span>
                <span className="shrink-0 tabular-nums text-xs text-[#7A6E5F] dark:text-[#B8A48C]">{formatCount(r.count, locale)}</span>
              </span>
              <span className="mt-1 block h-1 w-full rounded-full bg-[#EFE9DD] dark:bg-[#2A2116]" aria-hidden="true">
                <span className={`block h-1 rounded-full ${bar}`} style={{ width: `${Math.max(6, Math.round((r.count / max) * 100))}%` }} />
              </span>
            </a>
          </li>
        ))}
      </ol>
    </section>
  );
}

export interface WorkModelHeaderProps {
  stats: WorkModelHubStats;
  locale: Locale;
}

/**
 * Static SEO header for the remote and hybrid hubs and their combinations. Left: eyebrow, H1,
 * live count, intro. Right: the work-model share bar (remote / hybrid / on-site across every
 * open job in the locale index, the current model highlighted), the one thing on the page that
 * says how big remote work is here. Below: ranked lists with proportional bars for roles,
 * fields, cities and employers, then quiet chips to the parent pages and index hubs. All
 * server-rendered, plain anchors; rendered by SearchHeader.
 */
export const WorkModelHeader: React.FC<WorkModelHeaderProps> = ({ stats, locale }) => {
  const t = T[locale];
  const remote = stats.key === 'remote';
  const other = remote ? 'hybrid' : 'remote';
  const count = formatCount(stats.jobCount, locale);
  const { role, city, category } = stats.context;
  const categorySegment = locale === 'fi' ? 'kategoria' : locale === 'sv' ? 'kategori' : 'category';
  const isCombination = Boolean(role || city || category);

  const totals = stats.totals && stats.totals.total > 0 ? stats.totals : null;
  const pct = (n: number) => (totals ? Math.round((n / totals.total) * 100) : 0);
  const segments = totals
    ? [
        { key: 'remote' as const, label: t.remoteLabel, count: totals.remote, colour: 'bg-[#3B5E45] dark:bg-[#98CFA8]' },
        { key: 'hybrid' as const, label: t.hybridLabel, count: totals.hybrid, colour: 'bg-[#B98A1B] dark:bg-[#E9C46A]' },
        { key: 'onsite' as const, label: t.onsiteLabel, count: totals.onsite, colour: 'bg-[#CFC6B8] dark:bg-[#4A3F31]' },
      ]
    : [];

  const roleRows: RankedRow[] = stats.topRoles.map((r) => ({
    href: buildWorkModelHubPath(stats.key, locale, { roleSlug: r.slug }),
    label: locale === 'fi' ? finnishRolePhrase(r.name, remote ? 'etätyöt' : 'hybridityöt') : r.name,
    count: r.count,
  }));
  const fieldRows: RankedRow[] = stats.topFields
    .filter((f) => f.categorySlug)
    .map((f) => ({
      href: buildWorkModelHubPath(stats.key, locale, { categorySlug: f.categorySlug }),
      label: getCategoryDisplayName(f.categorySlug!, locale) ?? f.value,
      count: f.count,
    }));
  const cityRows: RankedRow[] = stats.topCities.map((c) => ({ href: buildWorkModelHubPath(stats.key, locale, { citySlug: c.slug }), label: c.name, count: c.count }));
  const employerRows: RankedRow[] = stats.topEmployers.map((e) => ({ href: companyProfileUrlPath(locale, e.id), label: e.name, count: e.count }));

  const upLinks: HubCrossLink[] = [
    ...(isCombination ? [{ kind: 'remote' as const, href: buildWorkModelHubPath(stats.key, locale), label: remote ? t.allRemote : t.allHybrid }] : []),
    ...(role ? [{ kind: 'role' as const, href: buildRoleHubPath(role.slug, locale), label: t.roleAll(roleInCopy(role.name, locale)) }] : []),
    ...(city ? [{ kind: 'location' as const, href: `/${locale}${buildLocationSrpPath(city.slug, locale)}`, label: t.cityAll(cityInCopy(city.name, locale)) }] : []),
    ...(category ? [{ kind: 'category' as const, href: `/${locale}/${LOCALIZED_PATHS[locale].jobs}/${categorySegment}/${category.slug}`, label: category.name }] : []),
    {
      kind: 'remote' as const,
      href: buildWorkModelHubPath(other, locale, { roleSlug: role?.slug ?? null, citySlug: city?.slug ?? null, categorySlug: category?.slug ?? null }),
      label: other === 'remote' ? t.allRemote : t.allHybrid,
    },
    ...buildHubIndexLinks(locale, ['roles', 'employers']),
  ];

  return (
    <section
      id="work-model-info-header"
      className="work-model-info-header relative overflow-hidden rounded-2xl border border-[#E3DCD0] bg-[#F8F5F0] p-5 dark:border-[#3E3325] dark:bg-[#170F07] sm:rounded-3xl sm:p-8 lg:p-10"
      aria-labelledby="work-model-header-title"
    >
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#7A6E5F] dark:text-[#B8A48C]">{remote ? t.eyebrowRemote : t.eyebrowHybrid}</p>
          <h1 id="work-model-header-title" className="font-display mt-2 text-3xl font-bold tracking-tight text-[#1E1A14] dark:text-[#F2EADF] sm:text-4xl lg:text-5xl" style={{ textWrap: 'balance' } as React.CSSProperties}>
            {buildWorkModelHeaderH1(stats, locale)}
          </h1>
          {stats.jobCount > 0 ? (
            <p className="mt-3 flex items-baseline gap-2">
              <span className="font-display text-3xl font-bold tabular-nums text-[#2F4A38] dark:text-[#98CFA8] sm:text-4xl">{count}</span>
              <span className="text-sm font-medium text-[#3D3529] dark:text-[#C4A882]">{t.openNow}</span>
            </p>
          ) : null}
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#3D3529] dark:text-[#C4A882]">{remote ? t.remoteIntro(count) : t.hybridIntro(count)}</p>
          {stats.englishSharePct != null && stats.englishSharePct > 0 ? (
            <p className="mt-2 text-sm text-[#7A6E5F] dark:text-[#B8A48C]">{t.englishShare(String(stats.englishSharePct))}</p>
          ) : null}
        </div>

        {/* The share card describes the whole work model; on a role/city/field combination it would read as that slice's share, so it stays on the bare hubs. */}
        {totals && !isCombination ? (
          <aside className="rounded-2xl border border-[#DDD5C8]/80 bg-white/85 p-5 dark:border-[#4A3520] dark:bg-[#1F1509]/90" aria-label={t.shareTitle}>
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7A6E5F] dark:text-[#B8A48C]">{t.shareTitle}</h2>
            <p className="font-display mt-2 text-2xl font-bold tabular-nums text-[#1E1A14] dark:text-[#F2EADF]">
              {t.shareOfAll(String(pct(remote ? totals.remote : totals.hybrid)))}
            </p>
            <div className="mt-3 flex h-3 w-full overflow-hidden rounded-full bg-[#EFE9DD] dark:bg-[#2A2116]" aria-hidden="true">
              {segments.map((s) => (
                <span
                  key={s.key}
                  className={`${s.colour} ${s.key === stats.key ? '' : 'opacity-70'} h-3`}
                  style={{ width: `${Math.max(1.5, (s.count / totals.total) * 100)}%` }}
                />
              ))}
            </div>
            <ul className="mt-3 space-y-1.5">
              {segments.map((s) => {
                const current = s.key === stats.key;
                const href = s.key === 'onsite' ? null : buildWorkModelHubPath(s.key, locale);
                const label = (
                  <span className="flex items-center justify-between gap-3 text-sm">
                    <span className="flex items-center gap-2">
                      <span className={`inline-block h-2.5 w-2.5 rounded-sm ${s.colour}`} aria-hidden="true" />
                      <span className={current ? 'font-semibold text-[#1E1A14] dark:text-[#F2EADF]' : 'text-[#3D3529] dark:text-[#C4A882]'}>{s.label}</span>
                    </span>
                    <span className="tabular-nums text-[#7A6E5F] dark:text-[#B8A48C]">
                      {formatCount(s.count, locale)} · {pct(s.count)} %
                    </span>
                  </span>
                );
                return (
                  <li key={s.key}>
                    {href && !current ? (
                      <a href={href} className="block rounded-md hover:underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B5E45]">
                        {label}
                      </a>
                    ) : (
                      label
                    )}
                  </li>
                );
              })}
            </ul>
          </aside>
        ) : null}
      </div>

      {roleRows.length + fieldRows.length + cityRows.length + employerRows.length > 0 ? (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <RankedList heading={t.roles} rows={roleRows} locale={locale} tone="green" />
          <RankedList heading={t.fields} rows={fieldRows} locale={locale} tone="slate" />
          <RankedList heading={t.cities} rows={cityRows} locale={locale} tone="green" />
          <RankedList heading={t.employers} rows={employerRows} locale={locale} tone="amber" />
        </div>
      ) : null}

      <div className="mt-6 border-t border-[#E3DCD0] pt-5 dark:border-[#3E3325]">
        <HubCrossLinks variant="chips" links={upLinks} locale={locale} heading={hubLinksHeading(locale)} headingLevel="h3" />
      </div>
    </section>
  );
};
